import * as React from 'react';
import BBCodeEditor from "./BBCodeEditor";
import Button from 'react-bootstrap/Button';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { auth, db } from "../config/firebase";
import { getDocs, addDoc, deleteDoc, getDoc, doc, query, where, collection } from "firebase/firestore";
import { useAuthState } from 'react-firebase-hooks/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { faClipboard } from '@fortawesome/free-solid-svg-icons';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import { faEraser } from '@fortawesome/free-solid-svg-icons';
import { faArrowRotateLeft } from '@fortawesome/free-solid-svg-icons';
import Skeleton from '@mui/material/Skeleton';
import OutlinedInput from '@mui/material/OutlinedInput';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import SimpleBar from 'simplebar-react';
import BBCodeCopyAlert from './BBCodeCopyAlert';
import ShareCodeCopyAlert from "./ShareCodeCopyAlert";
import 'simplebar-react/dist/simplebar.min.css';
import "../style/BBCode.css";
import "../style/Variable.css";
import "../style/MenuButtons.css";
import "../style/PrimaryMenuButtons.css";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en-gb';
import ListMenu from "./ListMenu.js";
import TemplateDeleteButton from "./TemplateDeleteButton.js";
import MenuItem from '@mui/material/MenuItem';

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
    theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, .1)'
        : 'rgba(220, 220, 220, .3)', // Lighter grey color
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
    backgroundColor:
    theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, .05)'
        : 'rgba(245, 245, 245, .3)', // Even lighter grey color
}));

function BBCodeMenu(props) {

    const { backdropOpen, setBackdropOpen } = props;

    const [expanded, setExpanded] = React.useState('panel1');

    const handleChange = (panel) => (event, newExpanded) => {
      setExpanded(newExpanded ? panel : false);
      setBBCodeCopyAlertOpen(false);
      setShareCodeCopyAlertOpen(false);
    };

    const [editMode, setEditMode] = React.useState({editing: false, entryID: null});

    const [templateEntries, setTemplateEntries] = React.useState([]);

    const [user] = useAuthState(auth);

    const bbcodeCollectionRef = collection(db, "bbcode");
    
    const [shareCodeCopyAlertOpen, setShareCodeCopyAlertOpen] = React.useState(false);

    const [bbcodeCopyAlertOpen, setBBCodeCopyAlertOpen] = React.useState(false);

    const [shareCodeInputBoxOpen, setShareCodeInputBoxOpen] = React.useState(false);

    const inputRefs = React.useRef({});

    const shareInputRef = React.useRef();

    const [clear, setClear] = React.useState(null);
    const [reset, setReset] = React.useState(null);

    const [dateMap, setDateMap] = React.useState(new Map([]));

    const createEntry = async () => {
        // Find the highest index value among existing entries
        const highestIndex = templateEntries.length > 0 ? Math.max(...templateEntries.map(entry => entry.index)) : 0;
    
        const entryData = await addDoc(bbcodeCollectionRef, {
            index: highestIndex + 1,
            title: "Untitled Template",
            user: auth.currentUser.email,
            locale: "en",
            template: "**Paste BBCode Template Here**",
            vars: []
        });
        setEditMode({editing: true, entryID: entryData.id});
    };
    
    const addFromShare = async (entryID) => {
        try {
            const entryDoc = await getDoc(doc(db, 'bbcode', entryID));
            if (entryDoc.exists()) {
                const entryData = entryDoc.data();
    
                // Find the highest index value among existing entries
                const highestIndex = templateEntries.length > 0 ? Math.max(...templateEntries.map(entry => entry.index)) : 0;
    
                await addDoc(bbcodeCollectionRef, {
                    index: highestIndex + 1,
                    title: entryData.title,
                    user: auth.currentUser.email,
                    locale: entryData.locale,
                    template: entryData.template,
                    vars: entryData.vars
                });
                getEntries();
            }
            else {
                alert("Error: Invalid share code. Please check the share code and try again.");
            }
        } catch (err) {
            alert("Error: Invalid share code. Please check the share code and try again.");
            console.error(err);
        }
    };
    

    const deleteEntry = async (entry) => {
        const entryDoc = doc(db, "bbcode", entry);
        await deleteDoc(entryDoc);
        getEntries();
    };

    const getEntries = async () => {
        try {
            const q = query(bbcodeCollectionRef, where("user", "==", auth.currentUser.email))
            const data = await getDocs(q);
            const filteredData = data.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id,
            }));
            setTemplateEntries(filteredData);
            console.log(filteredData);
        } catch (err) {
            console.error(err);
        }
    };

    const [authUser, setAuthUser] = React.useState(null);

    function removeSpecificUnicodeChars(str) {
        // Regular expression to match the specific Unicode characters (LRI and PDI)
        const unicodeCharsRegex = /[\u2066\u2069]/g;
        return str.replace(unicodeCharsRegex, '');
    }

    function removeSpacesAroundSlash(input) {
        return input.replace(/\s*\/\s*/g, '/');
    }

    const copyBBCode = async (templateID) => {
        try {
            const templateEntry = templateEntries.find((entry) => entry.id === templateID);
            if (!templateEntry) {
                console.error('Template not found:', templateID);
                alert('Template not found.');
                return;
            }
    
            let replacedTemplate = templateEntry.template;
    
            templateEntry.vars.forEach((variable) => {
                var variableValue = '';
                const variablePlaceholder = `{${variable.id}}`;
    
                if (variable.type === "text" || variable.type === "text_long"){
                    const variableInput = inputRefs.current[`${templateID}_${variable.id}`];
                    variableValue = variableInput ? removeSpecificUnicodeChars(variableInput.value) : '';
                } else if (variable.type === "date" || variable.type === "time" || variable.type === "datetime") {
                    const variableInput = inputRefs.current[`${templateID}_${variable.id}`];
                    variableValue = variableInput ? removeSpecificUnicodeChars(removeSpacesAroundSlash(variableInput.value)) + " " + dateMap.get(`${templateID}_${variable.id}_tz`) : '';
                } else if (variable.type === "hyperlink") {
                    const variableInputURL = inputRefs.current[`${templateID}_${variable.id}_url`];
                    const variableInputText = inputRefs.current[`${templateID}_${variable.id}_text`];
                    const variableURL = variableInputURL ? variableInputURL.value : '';
                    const variableText = variableInputText ? variableInputText.value : '';
                    if (variableText !== "") {
                        variableValue = "[url=" + variableURL + "]" + variableText + "[/url]"
                    } else {
                        variableValue = "[url]" + variableURL + "[/url]"
                    }
                } else if (variable.type === "image_url") {
                    const variableInput = inputRefs.current[`${templateID}_${variable.id}`];
                    variableValue = variableInput ? "[img]" + variableInput.value + "[/img]" : '';
                } else if (variable.type === "ordered_list") {
                    const variableInput = inputRefs.current[`${templateID}_${variable.id}`];
                    if (variableInput) {
                        const lines = variableInput.value.split('\n');
                        variableValue = "[ol]" + lines.map(line => "[li]" + line + "[/li]").join('\n') + "[/ol]";
                    } else {
                        variableValue = '';
                    }
                } else if (variable.type === "unordered_list") {
                    const variableInput = inputRefs.current[`${templateID}_${variable.id}`];
                    if (variableInput) {
                        const lines = variableInput.value.split('\n');
                        variableValue = "[ul]" + lines.map(line => "[li]" + line + "[/li]").join('\n') + "[/ul]";
                    } else {
                        variableValue = '';
                    }
                }
    
                replacedTemplate = replacedTemplate.split(variablePlaceholder).join(variableValue);
            });
    
            await navigator.clipboard.writeText(replacedTemplate);
        } catch (err) {
            console.error('Failed to copy BBCode to clipboard:', err);
            alert('Failed to copy BBCode to clipboard.');
        }
    };
    
    const clearTemplateInputs = (templateId, variables, locale) => {
        setClear(templateId);

        const updatedMap = new Map(dateMap);

        variables.forEach((variable) => {
            const inputRefKey = `${templateId}_${variable.id}`;
            const inputRef = inputRefs.current[inputRefKey];

            if (dateMap.get(inputRefKey + '_tz')) {
                updatedMap.set(inputRefKey + '_tz', "");
            }
        
            switch (variable.type) {
                case 'text':
                case 'text_long':
                case 'image_url':
                    inputRef.value = '';
                    break;
                case 'date':
                case 'time':
                case 'datetime':
                    updatedMap.set(inputRefKey, dayjs(new Date('')));
                    break;
                case 'hyperlink':
                    inputRefs.current[`${inputRefKey}_url`].value = '';
                    inputRefs.current[`${inputRefKey}_text`].value = '';
                    break;
                case 'ordered_list':
                case 'unordered_list':
                    break;
                default:
                    break;
            }
        });

        setDateMap(updatedMap);
    };

    const resetTemplateDefaults = (templateId, variables, locale) => {
        setReset(templateId);

        const updatedMap = new Map(dateMap);
        
        variables.forEach((variable) => {
            const inputRefKey = `${templateId}_${variable.id}`;
            const inputRef = inputRefs.current[inputRefKey];

            if (dateMap.get(inputRefKey + '_tz')) {
                updatedMap.set(inputRefKey + '_tz', "");
            }
        
            switch (variable.type) {
                case 'text':
                case 'text_long':
                case 'image_url':
                    inputRef.value = variable.default;
                    break;
                case 'date':
                case 'time':
                case 'datetime':
                    updatedMap.set(inputRefKey, dayjs(new Date(variable.default)));
                    break;
                case 'hyperlink':
                    inputRefs.current[`${inputRefKey}_url`].value = variable.default.url;
                    inputRefs.current[`${inputRefKey}_text`].value = variable.default.displayText;
                    break;
                case 'ordered_list':
                case 'unordered_list':
                    break;
                default:
                    break;
            }
          
        });

        setDateMap(updatedMap);
    };
      

    React.useEffect(() => {
        auth.onAuthStateChanged((u) => {
            if (u) {
                setAuthUser(true);
                getEntries();
            } else {
                setAuthUser(false);
            }
        });
    }, []);

    return(
        <React.Fragment>
            <div id="BBCode_Menu" className="BBCode_Container">
                {editMode.editing ? 
                    <BBCodeEditor entryID={editMode.entryID} /> : 
                    <div className="primary" style={{width: "calc(100vw - 200px)", justifyContent: "center"}}>
                        <div className="Add_Entry_Button">
                            {user ? 
                                <Button variant="dark" onClick={createEntry}><FontAwesomeIcon icon={faPlus} /> New Template</Button> : 
                                <Tooltip title="Sign in to create a template"><div style={{display: "inline-flex", justifyContent: "center"}}><Button variant="dark" disabled style={{width: "100%"}}><FontAwesomeIcon icon={faPlus} /> New Template</Button></div></Tooltip>
                            }
                            <div className="Padding_Div"></div>
                            {user ? 
                                <Button variant="dark" onClick={() => setShareCodeInputBoxOpen((shareCodeInputBoxOpen)=>!shareCodeInputBoxOpen)}><FontAwesomeIcon icon={faPlus} /> Add from Share Code</Button> : 
                                <Tooltip title="Sign in to add a template"><div style={{display: "inline-flex", justifyContent: "center"}}><Button variant="dark" disabled style={{width: "100%"}}><FontAwesomeIcon icon={faPlus} /> Add from Share Code</Button></div></Tooltip>
                            }
                            <Collapse in={shareCodeInputBoxOpen}>
                                <div id="shareTextInput" style={{display: "flex", marginTop: 10, padding: 5, alignItems: "center", justifyContent: "center", backgroundColor: "white", boxShadow: `0px 1px 2px black`, borderRadius: 5}}>
                                    <OutlinedInput placeholder="Share Code" size="small" id="shareInputBox" ref={shareInputRef} type="text" style={{width: 230}}></OutlinedInput>
                                    <div style={{padding: 5}}></div>
                                    <Button size="sm" onClick={() => addFromShare(shareInputRef.current.querySelector("input").value)} style={{minWidth: 59.61}}><FontAwesomeIcon icon={faPlus} /> Add</Button>
                                </div>
                            </Collapse>
                        </div>
                        <div className="Entry_List" style={{display: "inline-block", maxWidth: "1500px"}}>
                            <h2 style={{textAlign: "left", paddingBottom: 14}}>BBCode Templates</h2>
                            <SimpleBar className="Bar_Scroll">
                                <Stack spacing={2}>
                                    {authUser===false ? null :
                                        authUser===true ?
                                            templateEntries.sort((a, b) => a.index - b.index).map((template) => (
                                                <LocalizationProvider key={template.id} dateAdapter={AdapterDayjs} adapterLocale={template.locale}>
                                                    <Accordion key={template.id} expanded={expanded === template.id} onChange={handleChange(template.id)}>
                                                        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                                                            <Typography>{template.title}</Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails style={{justifyContent: "start", alignItems: "start"}}>
                                                            {template.vars.map((variable) => (
                                                                <div key={variable.id} style={{textAlign: "left"}}>
                                                                    <h3 className="variable-header" style={{paddingBottom: 2}}>{variable.name}</h3>
                                                                    <div style={{paddingBottom: 8}}><Typography>{variable.description}</Typography></div>
                                                                    {variable.type === "text" ? <TextField size="small" className="Text" id={`${template.id}_${variable.id}`} inputRef={(el) => (inputRefs.current[`${template.id}_${variable.id}`] = el)} defaultValue={variable.default}></TextField> :null}
                                                                    {variable.type === "text_long" ? <TextField size="small" className="Text_Long" multiline id={`${template.id}_${variable.id}`} inputRef={(el) => (inputRefs.current[`${template.id}_${variable.id}`] = el)} defaultValue={variable.default}></TextField> :null}
                                                                    {variable.type === "date" ? <div style={{display: "flex"}}><DatePicker className="Datetime" label="Date" id={`${template.id}_${variable.id}`} inputRef={(el) => (inputRefs.current[`${template.id}_${variable.id}`] = el)} value={dateMap.get(`${template.id}_${variable.id}`) ?? dayjs(new Date(variable.default))} onChange={(newValue) => {const updatedMap = new Map(dateMap); updatedMap.set(`${template.id}_${variable.id}`, newValue); setDateMap(updatedMap);}} slotProps={{ textField: { size: 'small' } }}></DatePicker><TextField select sx={{minWidth: 80}} size="small" label="Timezone" value={dateMap.get(`${template.id}_${variable.id}_tz`) ?? ''} onChange={(e) => {const updatedMap = new Map(dateMap); updatedMap.set(`${template.id}_${variable.id}_tz`, e.target.value); setDateMap(updatedMap);}}><MenuItem value=""><em>None</em></MenuItem><MenuItem value="UTC">UTC</MenuItem><MenuItem value="EST">EST</MenuItem><MenuItem value="EDT">EDT</MenuItem><MenuItem value="PST">PST</MenuItem><MenuItem value="PDT">PDT</MenuItem><MenuItem value="GMT">GMT</MenuItem><MenuItem value="BST">BST</MenuItem></TextField></div> :null}
                                                                    {variable.type === "time" ? <div style={{display: "flex"}}><TimePicker className="Datetime" label="Time" id={`${template.id}_${variable.id}`} inputRef={(el) => (inputRefs.current[`${template.id}_${variable.id}`] = el)} value={dateMap.get(`${template.id}_${variable.id}`) ?? dayjs(new Date(variable.default))} onChange={(newValue) => {const updatedMap = new Map(dateMap); updatedMap.set(`${template.id}_${variable.id}`, newValue); setDateMap(updatedMap);}} slotProps={{ textField: { size: 'small' } }}></TimePicker><TextField select sx={{minWidth: 80}} size="small" label="Timezone" value={dateMap.get(`${template.id}_${variable.id}_tz`) ?? ''} onChange={(e) => {const updatedMap = new Map(dateMap); updatedMap.set(`${template.id}_${variable.id}_tz`, e.target.value); setDateMap(updatedMap);}}><MenuItem value=""><em>None</em></MenuItem><MenuItem value="UTC">UTC</MenuItem><MenuItem value="EST">EST</MenuItem><MenuItem value="EDT">EDT</MenuItem><MenuItem value="PST">PST</MenuItem><MenuItem value="PDT">PDT</MenuItem><MenuItem value="GMT">GMT</MenuItem><MenuItem value="BST">BST</MenuItem></TextField></div> :null}
                                                                    {variable.type === "datetime" ? <div style={{display: "flex"}}><DateTimePicker className="Datetime" label="Date & Time" id={`${template.id}_${variable.id}`} inputRef={(el) => (inputRefs.current[`${template.id}_${variable.id}`] = el)} value={dateMap.get(`${template.id}_${variable.id}`) ?? dayjs(new Date(variable.default))} onChange={(newValue) => {const updatedMap = new Map(dateMap); updatedMap.set(`${template.id}_${variable.id}`, newValue); setDateMap(updatedMap);}} slotProps={{ textField: { size: 'small' } }}></DateTimePicker><TextField select sx={{minWidth: 80}} size="small" label="Timezone" value={dateMap.get(`${template.id}_${variable.id}_tz`) ?? ''} onChange={(e) => {const updatedMap = new Map(dateMap); updatedMap.set(`${template.id}_${variable.id}_tz`, e.target.value); setDateMap(updatedMap);}}><MenuItem value=""><em>None</em></MenuItem><MenuItem value="UTC">UTC</MenuItem><MenuItem value="EST">EST</MenuItem><MenuItem value="EDT">EDT</MenuItem><MenuItem value="PST">PST</MenuItem><MenuItem value="PDT">PDT</MenuItem><MenuItem value="GMT">GMT</MenuItem><MenuItem value="BST">BST</MenuItem></TextField></div> :null}
                                                                    {variable.type === "hyperlink" ? <div style={{display: "flex", alignItems: "center"}}><FontAwesomeIcon icon={faLink} style={{paddingRight: 7, color: "gray"}} /><TextField size="small" className="Hyperlink" label="URL" id={`${template.id}_${variable.id}`} inputRef={(el) => (inputRefs.current[`${template.id}_${variable.id}_url`] = el)} defaultValue={variable.default.url}></TextField><TextField size="small" className="Hyperlink" label="Display Text" id={`${template.id}_${variable.id}`} inputRef={(el) => (inputRefs.current[`${template.id}_${variable.id}_text`] = el)} defaultValue={variable.default.displayText}></TextField></div> :null}
                                                                    {variable.type === "image_url" ? <div style={{display: "flex", alignItems: "center"}}><FontAwesomeIcon icon={faImage} style={{paddingRight: 7, color: "gray"}} /><TextField size="small" className="Image_URL" label="URL" id={`${template.id}_${variable.id}`} inputRef={(el) => (inputRefs.current[`${template.id}_${variable.id}`] = el)} defaultValue={variable.default}></TextField></div> :null}
                                                                    {variable.type === "ordered_list" ? <ListMenu templateId={template.id} variableId={variable.id} clear={clear} setClear={setClear} reset={reset} setReset={setReset} setRefValue={(el) => (inputRefs.current[`${template.id}_${variable.id}`] = {value: el})} variableDefault={variable.default}></ListMenu> :null}
                                                                    {variable.type === "unordered_list" ? <ListMenu templateId={template.id} variableId={variable.id} clear={clear} setClear={setClear} reset={reset} setReset={setReset} setRefValue={(el) => (inputRefs.current[`${template.id}_${variable.id}`] = {value: el})} variableDefault={variable.default}></ListMenu> :null}
                                                                    {variable.inTemplate === false ? <Alert severity="warning" style={{marginTop: 10, width: 300, border:`1px solid #c69500`}}>Variable not added to template!</Alert> :null}
                                                                    <div style={{paddingTop: 25}}></div>
                                                                </div>
                                                            ))}
                                                            <div style={{display: "flex"}}>
                                                                <div className="Button_Group">
                                                                    <div style={{alignItems: "center", justifyContent: "center", display: "grid",  gridTemplateColumns: "auto auto auto auto", columnGap: 17}}>
                                                                        <Button className="Font_Major" variant="success" onClick={async () => {setBBCodeCopyAlertOpen(true); copyBBCode(template.id);}}><FontAwesomeIcon className="Icon_Size" icon={faClipboard} /> Copy BBCode</Button>
                                                                        <Button className="Font_Micro" variant="dark" style={{maxHeight: 30}} size="sm" onClick={() => resetTemplateDefaults(template.id, template.vars, template.locale)}><FontAwesomeIcon className="Icon_Size" icon={faArrowRotateLeft} /> Reset Template</Button>
                                                                        <Button className="Font_Micro" variant="dark" style={{maxHeight: 30}} size="sm" onClick={() => clearTemplateInputs(template.id, template.vars, template.locale)}><FontAwesomeIcon className="Icon_Size" icon={faEraser} /> Clear Template</Button>
                                                                    </div>
                                                                    <div style={{alignItems: "center", justifyContent: "center", display: "grid", gridTemplateColumns: "auto auto auto auto", columnGap: 17}}>
                                                                        <Button className="Font_Micro" style={{maxHeight: 30}} size="sm" onClick={async () => {await navigator.clipboard.writeText(template.id); setShareCodeCopyAlertOpen(true);}}><FontAwesomeIcon className="Icon_Size" icon={faCopy} /> Copy Share Code</Button>
                                                                        <Button className="Font_Micro" style={{maxHeight: 30}} size="sm" onClick={() => setEditMode({editing: true, entryID: template.id})}><FontAwesomeIcon className="Icon_Size" icon={faPencil} /> Edit</Button>
                                                                        <TemplateDeleteButton deleteTemplate={deleteEntry} templateId={template.id} backdropOpen={backdropOpen} setBackdropOpen={setBackdropOpen}></TemplateDeleteButton>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <BBCodeCopyAlert open={bbcodeCopyAlertOpen} setBBCodeCopyAlertOpen={setBBCodeCopyAlertOpen} />
                                                            <ShareCodeCopyAlert open={shareCodeCopyAlertOpen} setShareCodeCopyAlertOpen={setShareCodeCopyAlertOpen} />
                                                        </AccordionDetails>
                                                    </Accordion>
                                                </LocalizationProvider>
                                            )) :
                                            <div>
                                                <Skeleton variant="rounded" className="Entry_Skeleton" sx={{ bgcolor: 'grey.700', }} height={50} />
                                                <div style={{paddingTop:15}}></div>
                                                <Skeleton variant="rounded" className="Entry_Skeleton" sx={{ bgcolor: 'grey.700' }} height={50} />
                                                <div style={{paddingTop:15}}></div>
                                                <Skeleton variant="rounded" className="Entry_Skeleton" sx={{ bgcolor: 'grey.700' }} height={50} />
                                            </div>
                                    }
                                </Stack>
                            </SimpleBar>
                        </div>
                    </div>
                }
            </div>
        </React.Fragment>
    );
}

export default BBCodeMenu;